/**
 * Header avatar pill + dropdown menu (#2579, EPIC #2575 Tier 4).
 *
 * Replaces the bare `{full_name} ({role})` header text (+ the standalone logout
 * button) in BOTH shells with an avatar pill: a token-coloured initials circle +
 * name that opens a small anchored menu — Profile/Settings (→ /crm/settings),
 * theme toggle, and Sign out (absorbing the old standalone logout button).
 *
 * This is a lightweight anchored MENU (`role="menu"` / `role="menuitem"`), NOT a
 * full-screen dialog, so it correctly does NOT use the shared `<Modal>` (which is
 * for `fixed inset-0` dialogs). It is a click-outside / Esc-dismiss popover
 * positioned below the pill. Keyboard: the trigger is a button with
 * `aria-haspopup="menu"` + `aria-expanded`; Esc closes and returns focus.
 *
 * Mobile: the pill shrinks to the avatar circle alone (name hidden below `md:`),
 * keeping a ≥44px touch target; the menu is right-aligned so it never clips.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Moon, Settings, Sun } from "lucide-react";
import { useAuth } from "../../contexts/auth";
import { useTheme } from "../../hooks/useTheme";
import { ROLE_CATEGORY_LABELS, roleCategoryOf } from "../../api/users";

function initialsOf(name?: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return "?";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? first;
  return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase() || "?";
}

/** The settings route lives in the CRM shell; both shells route there. */
const SETTINGS_ROUTE = "/crm/settings";

export function UserMenu() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // #3784: show the role CATEGORY (Canvasser, HR / Compliance, ...), not only
  // the hard agent/admin role, matching the user-management list.
  const roleLabel = ROLE_CATEGORY_LABELS[
    roleCategoryOf({ role: user?.role ?? "agent", permission_preset: user?.permission_preset })
  ];
  const displayName = user?.full_name || user?.username || "Account";
  const initials = initialsOf(user?.full_name || user?.username);

  // Close on click-outside + Esc; return focus to the trigger on Esc.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${displayName}`}
        className="flex items-center gap-2 min-h-[44px] pl-1 pr-1 md:pr-2.5 rounded-full hover:bg-[var(--color-bg-page)] transition-colors"
      >
        <span className="flex items-center justify-center h-8 w-8 rounded-full bg-brand-accent text-[#ffffff] text-xs font-semibold shrink-0">
          {initials}
        </span>
        <span className="hidden md:flex flex-col items-start leading-tight min-w-0">
          <span className="text-sm font-medium text-[var(--color-text-primary)] truncate max-w-[10rem]">
            {displayName}
          </span>
          <span className="text-xs text-[var(--color-text-muted)]">
            {roleLabel}
          </span>
        </span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className="absolute right-0 top-full mt-2 w-56 z-50 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-lg py-1 crm-overlay-enter"
        >
          {/* Identity header inside the menu (mobile-friendly since the pill
              hides the name below md). */}
          <div className="px-3 py-2 border-b border-[var(--color-border)]">
            <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
              {displayName}
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">{roleLabel}</p>
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              navigate(SETTINGS_ROUTE);
            }}
            className="w-full flex items-center gap-2.5 min-h-[44px] px-3 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-page)] transition-colors"
          >
            <Settings className="h-4 w-4 text-[var(--color-text-secondary)]" />
            Profile &amp; settings
          </button>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              toggleTheme();
            }}
            className="w-full flex items-center gap-2.5 min-h-[44px] px-3 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-page)] transition-colors"
          >
            {theme === "light" ? (
              <Moon className="h-4 w-4 text-[var(--color-text-secondary)]" />
            ) : (
              <Sun className="h-4 w-4 text-yellow-400" />
            )}
            {theme === "light" ? "Dark mode" : "Light mode"}
          </button>

          <div className="my-1 border-t border-[var(--color-border)]" />

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              logout();
            }}
            className="w-full flex items-center gap-2.5 min-h-[44px] px-3 text-sm text-[var(--color-danger)] hover:bg-[var(--color-bg-page)] transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

